import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  hasRejectedOrdersNoticePopped,
  markRejectedOrdersNoticePopped,
  useRejectedOrders,
} from '../../../api/orderApi';
import type { OrderItem } from '../../../types';

/** 纯日历日直接截串显示：new Date() 重算会让 UTC 那套差一天的坑在这里复发 */
function dayOf(value: string | null): string {
  return value ? value.slice(0, 10) : '日期待定';
}

/**
 * 条目标题优先活动名称，没有就拿客户姓名顶上；副行随之让位——
 * 活动名称为空的单很常见（本地这两张就是），照直排会把「郭凯利」上下写两遍。
 */
function rowTitle(order: OrderItem): string {
  return order.activityName || order.customer?.fullName || '未命名订单';
}

function rowMeta(order: OrderItem): string {
  const title = rowTitle(order);
  const customerName = order.customer?.fullName;
  return [customerName && customerName !== title ? customerName : null, dayOf(order.orderStartDate)]
    .filter(Boolean)
    .join(' · ');
}

export default function RejectedOrdersNotice() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useRejectedOrders();
  const [open, setOpen] = useState(false);

  const total = data?.total ?? 0;
  const orders: OrderItem[] = data?.orders ?? [];

  // 一次登录只弹一次：会话结束由 authStore 复位（见 orderApi 里的开关说明）
  useEffect(() => {
    if (hasRejectedOrdersNoticePopped() || isLoading || total === 0) return;
    markRejectedOrdersNoticePopped();
    setOpen(true);
  }, [isLoading, total]);

  // 读数没落地不占位；读失败要说出来——「没读到」和「没有被驳回的单」是两件事，
  // 静默消失等于替用户把这条咽下去（App 端两态同此，见 docs/接口契约.md）。
  if (isLoading) return null;

  // 拿到过数据时失败态让位给提示行：那一行本身还是有效读数，
  // 与 App 端「有驳回单优先于读取失败」是同一条优先级。
  if (total === 0 && isError) {
    return (
      <div className="flex items-center gap-2.5 px-4 md:px-5 py-3 mb-3 bg-amber-50 border border-amber-100 rounded-xl">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
          <i className="ri-error-warning-line text-sm text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-700">被驳回订单读取失败</p>
          <p className="text-xs text-amber-600 mt-0.5">
            {error instanceof Error && error.message ? error.message : '网络异常，暂时没读到'}
            ，这不代表你没有被驳回的单
          </p>
        </div>
        <button
          onClick={() => void refetch()}
          className="text-xs text-amber-700 hover:text-amber-800 font-medium cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-lg hover:bg-amber-100"
        >
          重试
        </button>
      </div>
    );
  }

  if (total === 0) return null;

  const goToOrder = (orderId: string) => {
    setOpen(false);
    navigate('/orders', { state: { openOrderId: orderId } });
  };

  return (
    <>
      <div className="flex items-center gap-2.5 px-4 md:px-5 py-3 mb-3 bg-rose-50 border border-rose-100 rounded-xl">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 shrink-0">
          <i className="ri-shield-cross-line text-sm text-rose-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-rose-700">
            {total} 张订单被对方驳回，待你修改后重新提交
          </p>
          <p className="text-xs text-rose-500 mt-0.5">
            修改内容并重新提交、或改派其他客户之后，这条提示会自动消失
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="text-xs text-rose-600 hover:text-rose-700 font-medium cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-lg hover:bg-rose-100"
        >
          查看并修改
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50">
                  <i className="ri-shield-cross-line text-base text-rose-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">订单被驳回，待你修改重提</p>
                  <p className="text-xs text-gray-400 mt-0.5">共 {total} 张，改回待审核后不再提醒</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors rounded-lg hover:bg-gray-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-5 py-4 flex-1 overflow-y-auto space-y-2.5">
              {orders.map((order) => (
                <button
                  key={order.id}
                  onClick={() => goToOrder(order.id)}
                  className="w-full text-left px-3.5 py-3 bg-gray-50 hover:bg-rose-50 border border-gray-100 hover:border-rose-200 rounded-xl transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {rowTitle(order)}
                    </span>
                    <span className="text-xs text-gray-400 font-mono shrink-0">{order.orderNumber}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{rowMeta(order)}</p>
                  {order.reviewComment && (
                    <p className="text-xs text-rose-600 mt-1.5 leading-relaxed break-words">
                      对方驳回原因：{order.reviewComment}
                    </p>
                  )}
                </button>
              ))}
              {total > orders.length && (
                <p className="text-xs text-gray-500 text-center pt-1">
                  另有 {total - orders.length} 张未列出，可前往订单页查看
                </p>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 shrink-0 flex gap-2.5">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/orders');
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
              >
                前往订单页
              </button>
              <button
                onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all bg-rose-500 hover:bg-rose-600 text-white whitespace-nowrap"
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
